import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type {
  AlertRule,
  CreateAlertRuleRequest,
  Channel,
  CreateChannelRequest,
  Notification,
  ListResult,
} from '@/types'

// ── Alert rules ───────────────────────────────────────────────────────────────

export function useAlertRules(tenantId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['alert-rules', tenantId],
    queryFn: () =>
      api.get<ListResult<AlertRule>>(`/api/v1/tenants/${tenantId}/alert-rules`),
    enabled: !!tenantId,
  })
}

export function useCreateAlertRule(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (req: CreateAlertRuleRequest) =>
      api.post<AlertRule>(`/api/v1/tenants/${tenantId}/alert-rules`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-rules', tenantId] }),
  })
}

export function useDeleteAlertRule(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (ruleId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/alert-rules/${ruleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-rules', tenantId] }),
  })
}

export function useToggleAlertRule(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ ruleId, enabled }: { ruleId: string; enabled: boolean }) =>
      api.put<AlertRule>(`/api/v1/tenants/${tenantId}/alert-rules/${ruleId}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-rules', tenantId] }),
  })
}

// ── Channels ──────────────────────────────────────────────────────────────────

export function useChannels(tenantId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['channels', tenantId],
    queryFn: () =>
      api.get<ListResult<Channel>>(`/api/v1/tenants/${tenantId}/channels`),
    enabled: !!tenantId,
  })
}

export function useCreateChannel(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (req: CreateChannelRequest) =>
      api.post<Channel>(`/api/v1/tenants/${tenantId}/channels`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', tenantId] }),
  })
}

export function useDeleteChannel(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (channelId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/channels/${channelId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', tenantId] }),
  })
}

// ── Notification history ──────────────────────────────────────────────────────

export function useNotifications(tenantId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['notifications', tenantId],
    queryFn: () =>
      api.get<ListResult<Notification>>(`/api/v1/tenants/${tenantId}/notifications`),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  })
}
